// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Login Screen (email + password)
// ---------------------------------------------------------------------------

import { useState, useRef, useEffect } from 'react';
import { useAppStore } from '@/store/app-store';
import { useAuthStore } from '@/store/auth-store';
import { authService } from '@/services/auth-service';

export default function LoginScreen() {
  const showToast = useAppStore((s) => s.showToast);
  const setAuth = useAuthStore((s) => s.setAuth);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Autofocus email on mount
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!email.trim()) {
      setError('Entrez votre adresse email');
      emailRef.current?.focus();
      return;
    }
    if (!password) {
      setError('Entrez votre mot de passe');
      passwordRef.current?.focus();
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const data = await authService.login(email.trim(), password);

      // Extract user, company, and permissions from login response
      const user = data.user ?? data.identity ?? null;
      const company = data.active_company ?? data.company ?? null;
      const permissions = data.permissions ?? {};

      if (!user) {
        throw new Error('Reponse serveur invalide');
      }

      setAuth(user, company, permissions);
      showToast(`Bienvenue ${user.first_name ?? user.email}`, 'success');
    } catch (err) {
      const msg = (err as Error).message || 'Identifiants incorrects';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col items-center justify-center bg-surface px-4 py-8">
      <div className="w-full max-w-sm">
        {/* Header with POS icon */}
        <div className="text-center mb-10">
          <div className="mx-auto mb-4 w-16 h-16 bg-accent/20 rounded-2xl flex items-center justify-center">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-accent">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Caisse Tablette</h1>
          <p className="text-gray-400 text-sm">Connectez-vous pour demarrer</p>
        </div>

        {/* Login form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label htmlFor="login-email" className="block text-sm text-gray-300 mb-1.5">
              Adresse email
            </label>
            <input
              ref={emailRef}
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              placeholder="nom@entreprise.fr"
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="email"
              spellCheck={false}
              disabled={isLoading}
              className="w-full bg-surface-card text-white rounded-xl px-4 py-3 text-sm outline-none border border-white/10 focus:border-accent transition-colors placeholder-gray-500 disabled:opacity-50"
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="login-password" className="block text-sm text-gray-300 mb-1.5">
              Mot de passe
            </label>
            <div className="relative">
              <input
                ref={passwordRef}
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="Votre mot de passe"
                autoComplete="current-password"
                disabled={isLoading}
                className="w-full bg-surface-card text-white rounded-xl px-4 py-3 pr-12 text-sm outline-none border border-white/10 focus:border-accent transition-colors placeholder-gray-500 disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white p-1"
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <p className="text-danger text-sm text-center py-1">{error}</p>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                Connexion...
              </>
            ) : (
              'Se connecter'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
